import { useMemo, useRef, useState } from "preact/hooks";

type Options<T> = {
	readonly key: string;
	readonly defaultValue: T;
	readonly parse: (raw: string) => T;
	readonly stringify: (value: T) => string | undefined;
};

function useLocalStorage<T>({
	key,
	defaultValue,
	parse,
	stringify,
}: Options<T>): Readonly<[T, (value: T) => void]> {
	const [value, setValue] = useState<T>(() => {
		let rawValue: string | null;
		try {
			rawValue = localStorage.getItem(key);
		} catch {
			// Storage unavailable (private mode / sandboxed iframe) - use the default.
			return defaultValue;
		}

		// Not yet stored
		if (rawValue === null) {
			return defaultValue;
		}

		// Try and parse
		try {
			return parse(rawValue);
		} catch (e) {
			console.error(`Error parsing local storage ${key} value ${rawValue}`, e);
			return defaultValue;
		}
	});

	const defaultValueRef = useRef(defaultValue);
	defaultValueRef.current = defaultValue;
	const stringifyRef = useRef(stringify);
	stringifyRef.current = stringify;

	// The stored value is read once for the initial key and never re-read, so a
	// changing key is not supported (reads would stay on the old key while writes
	// target the new one). Surface it loudly rather than silently misbehaving.
	const initialKeyRef = useRef(key);
	if (initialKeyRef.current !== key) {
		console.error(
			`useLocalStorage does not support a changing key (was "${initialKeyRef.current}", now "${key}"); the value will keep reflecting the original key.`,
		);
	}

	return useMemo(
		() => [
			value,
			(newValue: T) => {
				setValue(newValue);

				try {
					if (newValue === defaultValueRef.current) {
						localStorage.removeItem(key);
						return;
					}

					const newStringified = stringifyRef.current(newValue);
					if (newStringified === undefined) {
						localStorage.removeItem(key);
					} else {
						localStorage.setItem(key, newStringified);
					}
				} catch {
					// Storage unavailable - keep the in-memory value only.
				}
			},
		],
		[value, key, defaultValueRef],
	);
}

export default useLocalStorage;
